import React, { useMemo, useRef, type FC } from 'react'

import { useIntl } from 'react-intl'

import {
  FormElement,
  SELECT_VARIANT_WITH_CHECKBOX,
  Select,
} from 'mailup-web-components'

import useAsyncSelect2 from 'Hooks/useAsyncSelect/useAsyncSelect'

import { useRecoilCallback, useRecoilValue } from 'recoil'

import { changeCriterionCallback } from 'Pages/RecipientsList/recoil/callbacks/filters'
import { criterionGroupsState } from 'Pages/RecipientsList/recoil/state/filters'

import {
  fetchGroupById,
  fetchGroups,
} from 'Pages/RecipientsList/recoil/services/asyncSelectGroups'

import { uuid } from 'Helpers/uuid'

import { INPUT_MESSAGES_MAX_ITEMS } from 'Constants'

interface InputGroupsProps {
  index: number
}

const InputGroups: FC<InputGroupsProps> = ({ index }) => {
  const intl = useIntl()
  const dirty = useRef(false)
  const { current: key } = useRef(uuid())

  const item = useRecoilValue(criterionGroupsState(index))

  const { options, loading, handleSearch, hint, hintNoResults } =
    useAsyncSelect2({
      key,
      value: item.values,
      maxItems: INPUT_MESSAGES_MAX_ITEMS,
      fetchItems: async () => await fetchGroups(''),
      fetchItemsByTerm: fetchGroups,
      fetchItemById: fetchGroupById,
      labels: {
        loading: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusLoading',
        }),
        noResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusNoMatches',
        }),
        moreResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusTooManyItems',
        }),
      },
    })

  const changeCriterion = useRecoilCallback(changeCriterionCallback, [])

  const handleChange = (v: number[]): void => {
    dirty.current = true

    void changeCriterion('values', v, index)
  }

  const handleExpandCollapse = (c: boolean): void => {
    if (!c) {
      handleSearch('')
    }
  }

  const memoOptions = useMemo(() => {
    return item.matchOperator === 'BelongsTo'
      ? [
          {
            value: 0,
            label: intl.formatMessage({
              id: 'Load.RecipientsList.RecipientsNoGroup',
            }),
          },
          ...options,
        ]
      : options
  }, [item.matchOperator, options, intl])

  const showFeedback = item.values.length === 0 && dirty.current

  return (
    <FormElement
      variant={showFeedback ? 'danger' : undefined}
      feedback={
        showFeedback
          ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
          : undefined
      }
    >
      <Select
        variant={SELECT_VARIANT_WITH_CHECKBOX}
        multiple
        value={item.values}
        options={memoOptions}
        loading={loading}
        onChange={handleChange}
        onSearch={handleSearch}
        onExpandCollapse={handleExpandCollapse}
        hint={hint}
        hintNoResults={hintNoResults}
        memoSelectOption
        error={showFeedback}
        placeholder={intl.formatMessage({
          id: 'Load.RecipientsList.SelectGroupPlaceholder',
        })}
        data-mu-test="input-group"
      />
    </FormElement>
  )
}

export default InputGroups
